import { registerLocaleData } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CSP_NONCE,
  HostListener,
  inject,
  isDevMode,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { EPermissions, IUserLogin, Language } from '@auth/login/data/login.model';
import { TranslateService } from '@ngx-translate/core';
import { ReceiptDownloadMonitorService } from '@pay/receipts/services/receipt-download-monitor.service';
import { COOKIE_NAME } from '@shared/components/cookie-banner/cookie-banner.component';
import { GoogleAnalyticsService } from '@shared/google-analytics/google-analytics.service';
import { AppVersionService } from '@shared/services/app-version/app-version.service';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { KeycloakActionNotifierService } from '@shared/services/auth/keycloak-action-notifier.service';
import { StorageBroadcastService } from '@shared/services/auth/storage-broadcast.service';
import { ChatbotService } from '@shared/services/chatbot/chatbot.service';
import { NavigationService } from '@shared/services/navigation/navigation-service.service';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import { PermissionService } from '@shared/services/util/permission.service';
import { CustomBranding, SessionFacade } from '@shared/store/session.facade';
import { INotification } from '@shared/store/session.model';
import { LanguageService } from '@shared/translate/language.service';
import { ModalService } from '@widgets/modal/modal.service';
import { environment } from 'environments/environment';
import { setAutoFreeze } from 'immer';
import { isEmpty } from 'lodash-es';
import md5 from 'md5';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { polyfill } from 'smoothscroll-polyfill';
import userflow, { Attributes } from 'userflow.js';

enum TagManagerCookieLevel {
  REQUIRED = '1',
  ALL = '2',
}

@Component({
  selector: 'app-root',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  notification$: Observable<INotification>;
  languagesLoaded: boolean;
  initialChangeLanguageLoading: boolean = true;
  sidebarCollapsed: boolean;
  userFlowActivated = false;
  chatbotActivated = false;

  loading: boolean = true;

  protected readonly environment = environment;

  private readonly cspNonce = inject(CSP_NONCE, { optional: true });
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly storageBroadcastService = inject(StorageBroadcastService);
  private readonly sessionFacade = inject(SessionFacade);
  private readonly permissionService = inject(PermissionService);
  private readonly topNotificationService = inject(TopNotificationService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly languageService = inject(LanguageService);
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly titleService = inject(Title);
  private readonly chatbotService = inject(ChatbotService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly cookieService = inject(CookieService);
  private readonly receiptDownloadMonitorService = inject(ReceiptDownloadMonitorService);
  private readonly navigationService = inject(NavigationService); // This inject is necessary
  private readonly keycloakActionNotifier = inject(KeycloakActionNotifierService);

  private readonly unsubscribe$ = new Subject<void>();
  private initScriptElement: HTMLScriptElement;
  private bootstrapScriptElement: HTMLScriptElement;
  private readonly CHATBOT_INIT_DELAY = 3000;
  private readonly VAYLENS_TAB_TITLE = 'vaylens';
  private readonly SPARKASSE_TAB_TITLE = 'S-eMobility Platform';
  private readonly ENERCITY_TAB_TITLE = 'eMobility Platform';
  private readonly KOMMA_TAB_TITLE = '1KOMMA5° Platform';
  private DEFAULT_TAB_TITLE = this.VAYLENS_TAB_TITLE;

  @HostListener('window:keydown.space', ['$event'])
  blockSpaceScroll(event: KeyboardEvent): void {
    const activeElement = document.activeElement as HTMLElement;
    const isInput =
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable;

    if (!isInput) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.notification$ = this.sessionFacade.notification$;
    this.sessionFacade.branding$.pipe(takeUntil(this.unsubscribe$)).subscribe(branding => {
      if (branding === CustomBranding.sparkasse) {
        this.DEFAULT_TAB_TITLE = this.SPARKASSE_TAB_TITLE;
      } else if (branding === CustomBranding.enercity) {
        this.DEFAULT_TAB_TITLE = this.ENERCITY_TAB_TITLE;
      } else if (branding === CustomBranding.komma) {
        this.DEFAULT_TAB_TITLE = this.KOMMA_TAB_TITLE;
      } else {
        this.DEFAULT_TAB_TITLE = this.VAYLENS_TAB_TITLE;
      }
    });

    combineLatest([
      this.sessionFacade.userSettings$,
      this.sessionFacade.userLogin$,
      this.sessionFacade.settingValueList$,
      this.sessionFacade.unauthNavigatedPage$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([settings, userLogin, settingValueList, unauthPage]) => {
        this.loading = false;
        this.cdr.detectChanges();

        // timeout is needed in case of wrong redirection between keycloak and portal
        setTimeout(() => {
          if (
            this.router.url === `/` &&
            (window.location.pathname === `/` || window.location.pathname === '')
          ) {
            if (unauthPage) {
              this.router.navigateByUrl(unauthPage);
              this.sessionFacade.saveUnauthNavigatedPageToStore(null);
            } else {
              this.router.navigate(['/monitor/dashboard']);
            }
          }
        }, 1000);
      });

    this.translate.onLangChange.pipe(take(2)).subscribe(() => {
      this.languagesLoaded = true;
      this.runLanguageChangeSpinner();
      this.receiptDownloadMonitorService.startMonitoring(); // message needs languages to be loaded
      // Flush any Keycloak AIA notifications (eg. UPDATE_PASSWORD, CONFIGURE_TOTP)
      // that were captured before translations were available.
      this.keycloakActionNotifier.flushPending();
    });
    this.disableDevFeatures();

    polyfill();

    this.googleAnalyticsService.listenToRouteChanges();
    this.appVersionService.listenToRouteChanges();

    this.closeModalsOnBackNavigation();
    this.setTabTitle();

    this.sessionFacade.sidebarCollapsed$.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
      this.sidebarCollapsed = value;
      this.cdr.detectChanges();
    });

    this.storageBroadcastService.start();
    this.checkKeycloakLoginState();
    this.setTranslationLanguage();
  }

  closeNotification() {
    this.topNotificationService.closeTopNotificiation();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (typeof window['initEmbeddedMessaging'] === 'function') {
      window['embeddedservice_bootstrap'].removeEventHandlers();
    }
  }

  isPageWithoutMenu(): boolean {
    const userLogin = this.sessionFacade.currentUser$.getValue();
    const pages = [document.location.origin + '/auth/passwordReset'];
    return !(userLogin && userLogin.settings?.email) && this.inPageList(pages);
  }

  isAnonymousUserPages(): boolean {
    const pages = ['/', '/auth/support', '/auth/passwordReset'];
    return this.inPageList(pages);
  }

  isFullWidthPage(): boolean {
    return [
      '/monitor/chargingInfrastructure',
      '/monitor/chargeRecords',
      '/operate/chargingInfrastructureDetail',
    ]
      .map(page => this.inPageRegardlessParameters(page))
      .some(is => is);
  }

  inPage(page: string): boolean {
    return this.router.url.split('?')[0] === page;
  }

  inPageList(pages: Array<string>) {
    let inList = false;
    pages.forEach(page => {
      if (this.inPage(page)) {
        inList = true;
      }
    });
    return inList;
  }

  inPageRegardlessParameters(page: string): boolean {
    return this.router.url.indexOf(page) !== -1;
  }

  private checkKeycloakLoginState() {
    this.authenticationService
      .getCurrentUserLoginPermissions()
      .pipe(
        take(1),
        switchMap(userLogin => {
          if (!isEmpty(userLogin)) {
            this.sessionFacade.loginSuccess({ ...userLogin, settings: undefined });
            return this.authenticationService.getSettingsUser();
          }
          return of(undefined);
        }),
        catchError(() => {
          this.sessionFacade.loginError();
          return of(undefined);
        })
      )
      .subscribe({
        next: settings => {
          this.sessionFacade.setUserSettings(settings);
          this.checkUserLogin();
        },
        error: () => {
          this.sessionFacade.setUserSettings(undefined);
        },
      });
  }

  private checkUserLogin() {
    combineLatest([this.sessionFacade.userLogin$, this.sessionFacade.settingValueList$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([userLogin, settings]) => {
        if (isEmpty(userLogin)) {
          this.userFlowActivated = false;
          this.deactivateAndRemoveChatbotScripts();
        }
        if (!isEmpty(userLogin) && isEmpty(settings)) {
          return;
        }
        const translationKeysEnabled = this.cookieService.get('TRANSLATION_KEYS') === 'true';
        this.setTranslationLanguage(translationKeysEnabled && !environment.production);
        const userData = userLogin as IUserLogin;
        const tagManagerCookieLevel: TagManagerCookieLevel =
          this.getGoogleTagManagerCookieLevel() ?? TagManagerCookieLevel.REQUIRED;
        if (
          !this.userFlowActivated &&
          (environment.userFlowWorkspaceToken || userData?.settings?.email?.includes('cypress')) &&
          userData?.settings?.email
        ) {
          this.initUserFlow(userData, tagManagerCookieLevel);
          this.userFlowActivated = true;
        }
      });

    this.sessionFacade.userLogin$
      .pipe(
        map(userLogin => userLogin?.settings?.email ?? null),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        if (
          environment.salesforceBootstrapScriptUrl &&
          this.permissionService.userHasPermission(EPermissions.VIEW_SALESFORCE_CHAT)
        ) {
          this.initSalesforceChatBot();
        }
      });
  }

  private initUserFlow(userData: IUserLogin, tagManagerCookieLevel: TagManagerCookieLevel) {
    const identifyObj = this.getUserForUserflowIdentify(userData, tagManagerCookieLevel);
    console.debug('Userflow mail: ' + identifyObj.email);
    console.debug('Userflow name: ' + identifyObj.name);
    if (identifyObj) {
      userflow.init(environment.userFlowWorkspaceToken.toString());
      userflow.identify(identifyObj.name as string, identifyObj);
      if (!userData.internalRole) {
        // This is needed for setting a company for the user
        userflow.group(userData.visualStyleId);
      }
    }
  }

  private getUserForUserflowIdentify(
    userData: IUserLogin,
    tagManagerCookieLevel: TagManagerCookieLevel
  ): Attributes {
    const emailDomain: string = `@${userData.settings.email.split('@')[1]}`;
    if (
      userData.settings.email !== 'cypress.admin@vaylens.com' && // needed in order to test cookie levels in cypress tests
      (emailDomain.includes('@compleo-cs.com') ||
        emailDomain.includes('@vaylens.com') ||
        emailDomain.includes('.vaylens.com'))
    ) {
      return {
        name: this.getHashedUser(userData.settings.email),
        email: `non${emailDomain}`,
        signed_up_at: moment().utc().format().toString(),
        locale_code: userData.settings.language?.toLocaleUpperCase(),
        isInternalUser: userData.internalRole,
        businessPartnerId: userData.internalRole ? null : userData.visualStyleId,
      };
    }

    switch (tagManagerCookieLevel) {
      case TagManagerCookieLevel.REQUIRED:
        return {
          name: this.getHashedUser(userData.settings.email),
          email: `non${emailDomain}`,
          signed_up_at: moment().utc().format().toString(),
          locale_code: userData.settings.language?.toLocaleUpperCase(),
          isInternalUser: userData.internalRole,
          businessPartnerId: userData.internalRole ? null : userData.visualStyleId,
        };
      case TagManagerCookieLevel.ALL:
        return {
          name: `${userData.settings.firstName} ${userData.settings.lastName}`,
          email: `${userData.settings.email}`,
          signed_up_at: moment().utc().format().toString(),
          locale_code: userData.settings.language?.toLocaleUpperCase(),
          isInternalUser: userData.internalRole,
          businessPartnerId: userData.internalRole ? null : userData.visualStyleId,
        };
      default:
        return;
    }
  }

  private getHashedUser(userId: string): string {
    return md5(userId);
  }

  private getGoogleTagManagerCookieLevel(): TagManagerCookieLevel {
    return document.cookie
      .split(';')
      .find(c => c.includes(COOKIE_NAME))
      ?.split('=')[1] as TagManagerCookieLevel;
  }

  private setTranslationLanguage(toggleTranslations?: boolean) {
    const language = toggleTranslations
      ? Language.TRANSLATION_KEYS
      : this.languageService.getUserLanguage();

    this.localeInitializer(
      language === Language.TRANSLATION_KEYS ? this.languageService.getUserLanguage() : language
    );

    this.translate.use(language);
  }

  private localeInitializer(language: Language) {
    const code = language.toString().substring(0, 2).toLowerCase();

    const angularLoaderMap: Record<string, () => Promise<any>> = {
      de: () => import('@angular/common/locales/de'),
      en: () => import('@angular/common/locales/en-GB'),
      fr: () => import('@angular/common/locales/fr'),
      hu: () => import('@angular/common/locales/hu'),
      it: () => import('@angular/common/locales/it'),
      sv: () => import('@angular/common/locales/sv'),
    };

    const momentLoaderMap: Record<string, () => Promise<any>> = {
      de: () => import('moment/locale/de'),
      fr: () => import('moment/locale/fr'),
      hu: () => import('moment/locale/hu'),
      it: () => import('moment/locale/it'),
      sv: () => import('moment/locale/sv'),
      en: () => import('moment/locale/en-gb'),
    };

    const angularLoader = angularLoaderMap[code];
    const momentLoader = momentLoaderMap[code];

    const tasks: Promise<any>[] = [];

    if (angularLoader) {
      tasks.push(angularLoader().then(m => registerLocaleData(m.default)));
    }
    if (momentLoader) {
      tasks.push(momentLoader());
    }

    Promise.all(tasks)
      .catch(() => void 0)
      .finally(() => {
        const momentCode = code === 'en' ? 'en-gb' : code;
        moment.locale(momentCode);
      });
  }

  private disableDevFeatures() {
    if (!isDevMode()) {
      // Disable auto-freeze feature of the immer library to improve performance
      setAutoFreeze(false);
    }
  }

  private closeModalsOnBackNavigation() {
    this.router.events
      .pipe(
        filter((event: NavigationStart) => event.navigationTrigger === 'popstate'),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => this.modalService.closeAllModals());
  }

  private setTabTitle() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          while (route!.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot.data['tabTitleKey']) {
            routeTitle = route!.snapshot.data['tabTitleKey'];
          }
          return routeTitle;
        }),
        switchMap(titleKey =>
          titleKey ? this.translate.stream(titleKey) : of(this.DEFAULT_TAB_TITLE)
        )
      )
      .subscribe((title: string) => {
        this.titleService.setTitle(title);
      });
  }

  private initSalesforceChatBot() {
    if (this.bootstrapScriptElement && this.initScriptElement) {
      return;
    }
    setTimeout(() => {
      this.chatbotService
        .getChatbotInitScript()
        .pipe(take(1))
        .subscribe((script: string) => {
          const adjustedScript = script
            .replace('__PARAM0__', this.languageService.getUserLanguageWithFallback())
            .replace('__PARAM1__', environment.salesforceBoostrapScriptParam1.toString())
            .replace('__PARAM2__', environment.salesforceBoostrapScriptParam2.toString())
            .replace('__PARAM3__', environment.salesforceBoostrapScriptParam3.toString())
            .replace('__PARAM4__', environment.salesforceBoostrapScriptParam4.toString())
            .replace('__PARAM5__', this.languageService.getUserLanguageWithFallback());

          this.activateChatbotInitScript(adjustedScript);
          this.activateChatbotBoostrapScript();
        });
    }, this.CHATBOT_INIT_DELAY);
  }

  private activateChatbotInitScript(script: string) {
    this.initScriptElement = document.createElement('script');
    this.initScriptElement.classList.add('embedded-messaging-init-script');
    if (this.cspNonce) {
      this.initScriptElement.nonce = this.cspNonce;
    }
    this.initScriptElement.text = script;
    document.body.appendChild(this.initScriptElement);
  }

  private activateChatbotBoostrapScript() {
    this.bootstrapScriptElement = document.createElement('script');
    this.bootstrapScriptElement.src = environment.salesforceBootstrapScriptUrl.toString();
    this.bootstrapScriptElement.classList.add('embedded-messaging-bootstrap-script');
    this.bootstrapScriptElement.defer = true;
    this.bootstrapScriptElement.setAttribute('as', 'script');
    this.setOnLoadChatbotBoostrapScript();
    document.body.appendChild(this.bootstrapScriptElement);
  }

  private setOnLoadChatbotBoostrapScript() {
    this.bootstrapScriptElement.onload = () => {
      if (typeof window['initEmbeddedMessaging'] === 'function') {
        window['initEmbeddedMessaging']();
        this.setLoadListenerToEmbeddedMessagingFrame();
        this.chatbotActivated = true;
        this.cdr.detectChanges();
      } else {
        console.debug('initEmbeddedMessaging function not found');
      }
    };
  }

  private setLoadListenerToEmbeddedMessagingFrame() {
    const interval = setInterval(() => {
      const embeddedMessagingFrame = document.getElementById(
        'embeddedMessagingFrame'
      ) as HTMLIFrameElement;
      const embeddedMessagingConversationButton = document.getElementById(
        'embeddedMessagingConversationButton'
      );
      if (
        embeddedMessagingConversationButton &&
        embeddedMessagingConversationButton.style.display !== 'block'
      ) {
        this.setChatbotIconPosition(embeddedMessagingConversationButton, false);
      }
      if (embeddedMessagingFrame) {
        this.setChatbotIconPosition(embeddedMessagingFrame);
      }
    }, 500);
  }

  private setChatbotIconPosition(
    embeddedMessagingElement: HTMLIFrameElement | HTMLElement,
    setBorderRadius = true
  ) {
    embeddedMessagingElement.style.right = setBorderRadius ? '37px' : '40px';
    if (setBorderRadius) {
      embeddedMessagingElement.style.borderRadius = '16px';
    }
    if (this.userFlowActivated) {
      embeddedMessagingElement.style.bottom = setBorderRadius ? '77px' : '80px';
    } else {
      embeddedMessagingElement.style.bottom = setBorderRadius ? '20px' : '23px';
    }
    embeddedMessagingElement.style.display = 'block';
    embeddedMessagingElement.classList.add('display-always');
  }

  private deactivateAndRemoveChatbotScripts() {
    if (window['embeddedservice_bootstrap']) {
      window['embeddedservice_bootstrap'].userVerificationAPI.clearSession(true).then(() => {
        window['embeddedservice_bootstrap'].utilAPI.removeAllComponents();
        const initScriptElementList = document.getElementsByClassName(
          'embedded-messaging-init-script'
        );
        for (let i = 0; i < initScriptElementList.length; i++) {
          (initScriptElementList[i] as HTMLScriptElement).remove();
        }
        const bootstrapScriptElementList = document.getElementsByClassName(
          'embedded-messaging-bootstrap-script'
        );
        for (let i = 0; i < bootstrapScriptElementList.length; i++) {
          (bootstrapScriptElementList[i] as HTMLScriptElement).remove();
        }
        this.initScriptElement = undefined;
        this.bootstrapScriptElement = undefined;
        this.chatbotActivated = false;
        this.cdr.detectChanges();
      });
    }
  }

  private runLanguageChangeSpinner() {
    this.initialChangeLanguageLoading = true;
    setTimeout(() => (this.initialChangeLanguageLoading = false), 1000);
  }
}
